<template>
	<div>
		<label
			v-if="label"
			class="mb-2 block text-sm font-medium text-gray-700"
		>
			{{ label }}
			<span v-if="required" class="text-red-500">*</span>
		</label>

		<input
			ref="fileInput"
			type="file"
			accept="image/*"
			:multiple="multiple"
			class="hidden"
			@change="handleFileSelection"
		/>

		<!-- Upload Button (when no images) -->
		<div
			v-if="modelValue.length === 0"
			class="flex h-24 cursor-pointer flex-row items-center justify-center gap-2 rounded-lg bg-[#eee] p-4"
			@click="openFileDialog"
		>
			<div class="flex flex-col items-center gap-2">
				<ion-icon
					:icon="cameraOutline"
					class="text-5xl text-gray-400"
				/>
			</div>
			<div>
				<p class="text-lg font-medium text-gray-700">
					{{ $t("common.add-photos") }}
				</p>
				<p class="text-sm text-gray-500">
					{{ $t("common.click-to-browse-files") }}
				</p>
			</div>
		</div>

		<!-- Images Display (note-style) -->
		<div v-if="modelValue.length > 0" class="space-y-4">
			<!-- Controls Bar -->
			<div class="flex items-center justify-between">
				<p class="text-sm text-gray-600">
					{{
						$t("common.photo-count", {
							count: modelValue.length,
						})
					}}
				</p>
				<div class="flex gap-2">
					<ion-button
						fill="outline"
						size="small"
						:color="deleteMode ? 'danger' : 'medium'"
						@click="toggleDeleteMode"
					>
						<ion-icon slot="start" :icon="trashOutline" />
						{{
							deleteMode
								? $t("common.exit-delete-mode")
								: $t("common.delete-photos")
						}}
					</ion-button>
				</div>
			</div>

			<!-- Photo Grid -->
			<photo-grid
				:photos="displayPhotos"
				:delete-mode="deleteMode"
				:show-hover-overlay="!deleteMode"
				@photo-click="handlePhotoClick"
			>
				<template #add-button>
					<div
						v-if="!deleteMode && multiple"
						class="flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400"
						@click="openFileDialog"
					>
						<ion-icon
							:icon="addOutline"
							class="text-2xl text-gray-400"
						/>
					</div>
				</template>
			</photo-grid>
		</div>

		<!-- Processing Progress -->
		<div v-if="isProcessing" class="mt-4">
			<div class="flex items-center gap-3 rounded-lg bg-blue-50 p-3">
				<!-- Spinner -->
				<div
					class="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
				></div>
				<!-- Text -->
				<div class="flex-1">
					<p class="text-sm font-medium text-blue-700">
						{{ t("common.processing-images") }}
					</p>
					<p class="text-xs text-blue-600">
						{{
							t("common.processing-x-of-y", {
								current: currentProcessingIndex,
								total: totalProcessingCount,
							})
						}}
					</p>
				</div>
			</div>
		</div>

		<!-- Error message -->
		<div v-if="error" class="mt-4 text-center">
			<p class="text-sm text-red-500">{{ error }}</p>
			<ion-button fill="clear" size="small" @click="clearError">
				{{ t("common.dismiss") }}
			</ion-button>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed } from "vue";
	import { useI18n } from "vue-i18n";
	import { IonIcon, IonButton } from "@ionic/vue";
	import { addOutline, cameraOutline, trashOutline } from "ionicons/icons";
	import { PhotoItem } from "seedgreen-shared/types/photo.types";
	import PhotoGrid, {
		type DisplayPhoto,
	} from "@/components/ui/PhotoGrid.vue";
	import { compressImage, fileToBase64 } from "@/utils/photos";

	interface Props {
		label?: string;
		required?: boolean;
		multiple?: boolean;
		maxFileSize?: number; // Maximum file size in bytes
	}

	const props = withDefaults(defineProps<Props>(), {
		label: undefined,
		required: false,
		multiple: false,
		maxFileSize: 50 * 1024 * 1024, // 50MB default
	});

	const modelValue = defineModel<PhotoItem[]>({ default: () => [] });

	const { t } = useI18n();

	const fileInput = ref<HTMLInputElement | null>(null);
	const deleteMode = ref(false);

	const error = ref<string | null>(null);
	const isProcessing = ref(false);
	const currentProcessingIndex = ref(0);
	const totalProcessingCount = ref(0);

	/** Transform PhotoItem to DisplayPhoto for PhotoGrid */
	const displayPhotos = computed<DisplayPhoto[]>(() => {
		return modelValue.value.map((photo) => {
			const dataUrl = `data:${photo.mimeType};base64,${photo.data}`;
			return {
				// No separate thumbnail for local photos
				fullSrc: dataUrl,
				name: photo.name,
			};
		});
	});

	/**
	 * Opens the native file picker dialog.
	 */
	const openFileDialog = () => {
		fileInput.value?.click();
	};

	/**
	 * Toggles between normal view mode and delete mode for managing photos.
	 */
	const toggleDeleteMode = () => {
		deleteMode.value = !deleteMode.value;
	};

	/**
	 * Validates a file to ensure it's a valid image within size limits.
	 * @param file - The file to validate
	 * @returns Error message if validation fails, null if valid
	 */
	const validateFile = (file: File): string | null => {
		// Check file type
		if (!file.type.startsWith("image/")) {
			return t("common.please-select-valid-image");
		}

		// Check file size using the configurable limit
		if (file.size > props.maxFileSize) {
			return t("common.file-size-too-large");
		}

		return null;
	};

	/**
	 * Processes and compresses image files for upload.
	 * Validates files, compresses them, and converts to base64 for preview.
	 * @param files - Array of files to process
	 * @returns Array of processed PhotoItem objects, or empty array if validation fails
	 */
	const processFiles = async (files: File[]): Promise<PhotoItem[]> => {
		const results: PhotoItem[] = [];
		const validFiles: File[] = [];

		// Validate all files first
		for (const file of files) {
			const validationError = validateFile(file);
			if (validationError) {
				error.value = validationError;
				return [];
			}
			validFiles.push(file);
		}

		if (validFiles.length === 0) return [];

		try {
			isProcessing.value = true;
			totalProcessingCount.value = validFiles.length;
			currentProcessingIndex.value = 0;

			// Process each valid file with progress tracking
			for (let i = 0; i < validFiles.length; i++) {
				const file = validFiles[i];
				currentProcessingIndex.value = i + 1;

				// Compress image once for both preview and upload
				const compressedFile = await compressImage(file, 0.85, 1600);

				// Convert to base64 for preview
				const base64Data = await fileToBase64(compressedFile);

				const photoItem: PhotoItem = {
					name: file.name,
					file: compressedFile, // Store compressed file (reused for upload)
					data: base64Data,
					mimeType: compressedFile.type,
				};

				results.push(photoItem);
			}

			return results;
		} catch (err) {
			console.error("Failed to process image:", err);
			error.value = t("common.failed-to-process-image");
			return [];
		} finally {
			isProcessing.value = false;
			currentProcessingIndex.value = 0;
			totalProcessingCount.value = 0;
		}
	};

	/**
	 * Handles file selection from the input element.
	 * Processes selected files and adds them to the model value.
	 * @param event - File input change event
	 */
	const handleFileSelection = async (event: Event) => {
		const target = event.target as HTMLInputElement;
		const files = Array.from(target.files || []);

		if (files.length === 0) return;

		// Clear errors
		error.value = null;
		const processedPhotos = await processFiles(files);

		if (processedPhotos.length > 0) {
			if (props.multiple) {
				modelValue.value = [...modelValue.value, ...processedPhotos];
			} else {
				// For single image, replace the existing one
				modelValue.value = [processedPhotos[0]];
			}
		}

		target.value = "";
	};

	/**
	 * Removes an image from the collection at the specified index.
	 * Exits delete mode if no images remain.
	 * @param index - Index of the image to remove
	 */
	const removeImage = (index: number) => {
		const newImages = [...modelValue.value];
		newImages.splice(index, 1);
		modelValue.value = newImages;

		// Exit delete mode if no images remain
		if (newImages.length === 0) {
			deleteMode.value = false;
		}
	};

	/**
	 * Handles clicks on photo items when in delete mode.
	 * PhotoGrid handles viewing internally when not in delete mode.
	 * @param index - Index of the clicked photo
	 */
	const handlePhotoClick = (index: number) => {
		removeImage(index);
	};

	/**
	 * Clears any error messages currently displayed.
	 */
	const clearError = () => {
		error.value = null;
	};

	defineExpose({
		openFileDialog,
		clearError,
	});
</script>
